import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { sessionQueryOptions, unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { cartRequestPageQueryOptions } from "~/app/api/pageData/cartRequestPageQuery";
import type {
  CartRequestCustomerData,
  CartRequestPageInitialData,
  CartRequestSalesPartnerData,
} from "~/app/api/pageData/cartRequestPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createB2CRequestPage, type B2CRequestPageProps } from "~/pages/B2CRequestPage/B2CRequestPage";
import type { SalesPartnerInfo, UserInfoData } from "~/features/cart/B2CRequestPage/types";
import { useSession } from "~/app/config/useSession";
import { userRolesAreAuthenticated } from "~/domains/user/utils/roles";
import { activeCartResourceId } from "~/domains/cart/utils/cartResourceIdentity";
import { occCartUserPathFor } from "~/app/api/occ/occUserPath";

const CART_REQUEST_PATH = "/cart/request";

function emptyIfMissing(value: string | undefined): string {
  return value ?? "";
}

/**
 * Reproduces the legacy {@code CartPageController#renderCartRequestPage} transform
 * verbatim. Quirks preserved:
 *  - {@code registered} is nested INSIDE {@code userInfo} (not a sibling prop).
 *  - Customer shipping address city reads {@code AddressData.getTown()} ({@code town}).
 *  - Sales-partner address city reads {@code AddressData.getTownName()} ({@code townName}).
 *  - The facade redirects instead of returning OK when customer or sales partner
 *    is absent. Nullable nested bean properties are normalized to empty strings
 *    at this UI boundary.
 */
function toUserInfo(customer: CartRequestCustomerData, registered: boolean): UserInfoData {
  const shippingAddress = customer.defaultShippingAddress;
  return {
    firstName: emptyIfMissing(customer.firstName),
    lastName: emptyIfMissing(customer.lastName),
    company: emptyIfMissing(shippingAddress?.companyName),
    line1: emptyIfMissing(shippingAddress?.line1),
    additionalLine1: emptyIfMissing(shippingAddress?.additionalLine1),
    line2: emptyIfMissing(shippingAddress?.line2),
    zipCode: emptyIfMissing(shippingAddress?.postalCode),
    city: emptyIfMissing(shippingAddress?.town),
    country: emptyIfMissing(shippingAddress?.country?.isocode),
    salutationCode: emptyIfMissing(customer.salutationCode),
    titleCode: emptyIfMissing(customer.titleCode),
    phone: emptyIfMissing(shippingAddress?.phone),
    email: emptyIfMissing(customer.email),
    registered,
  };
}

function toSalesPartner(salesPartner: CartRequestSalesPartnerData): SalesPartnerInfo {
  const partnerAddress = salesPartner.address;
  return {
    name: emptyIfMissing(salesPartner.name),
    displayName: emptyIfMissing(salesPartner.displayName),
    line1: emptyIfMissing(partnerAddress?.line1),
    line2: emptyIfMissing(partnerAddress?.line2),
    zipCode: emptyIfMissing(partnerAddress?.postalCode),
    city: emptyIfMissing(partnerAddress?.townName),
    country: emptyIfMissing(partnerAddress?.country?.isocode),
    affiliateId: emptyIfMissing(salesPartner.affiliateId),
    subsidiaryId: emptyIfMissing(salesPartner.subsidiaryId),
    phone: emptyIfMissing(partnerAddress?.phone),
    fax: emptyIfMissing(partnerAddress?.fax),
    email: emptyIfMissing(partnerAddress?.email),
  };
}

export function toCartRequestProps(data: CartRequestPageInitialData, registeredCartId?: string): B2CRequestPageProps {
  const { customer } = data;
  return {
    userInfo: toUserInfo(customer, data.registered),
    salesPartner: toSalesPartner(data.salesPartner),
    marketingConsent: customer.marketingConsent ?? false,
    cartId: registeredCartId ?? data.cartId,
    guestCheckoutContext: data.guestCheckoutContext,
  };
}

function CartRequestRoute() {
  const result = useLoaderData({
    from: CART_REQUEST_PATH,
  }) as PageInitialData<CartRequestPageInitialData>;
  const shellProps = useStorefrontShellProps();
  const session = useSession();
  const roles = session?.user.roles ?? [];
  const authenticated = userRolesAreAuthenticated(roles);
  const registeredCartId = authenticated ? activeCartResourceId(session?.activeCart, true) : undefined;

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createB2CRequestPage(toCartRequestProps(result.data, registeredCartId))}
    </StorefrontShellFrame>
  );
}

export async function loadCartRequestPageData(context: { queryClient: QueryClient }) {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions());
  const authenticated = userRolesAreAuthenticated(session.user.roles);
  const userPath = occCartUserPathFor(authenticated);
  const activeCartId = activeCartResourceId(session.activeCart, authenticated);
  const result = await context.queryClient.ensureQueryData(cartRequestPageQueryOptions(userPath, activeCartId));

  return unwrapPageInitialData(result);
}

export const cartRequestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CART_REQUEST_PATH,
  loader: async ({ context }) => loadCartRequestPageData(context),
  component: CartRequestRoute,
});
